import jsPDF from "jspdf";

/**
 * "Modulo contestuale" — autorizzazione contestuale firmata da entrambi i
 * genitori esercenti la potestà del minore. Documento accompagnatorio al
 * tesseramento FIGC che attesta il consenso congiunto di Padre e Madre.
 *
 * Layout: A4 portrait, margini generosi 56pt, due colonne per i dati e per le
 * firme, anti-collisione con calcolo larghezze esplicito.
 */
export async function downloadContestualePdf({ team, player }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 56;
  const contentW = pageW - margin * 2;
  let y = 56;

  // ----- Header (logo + denominazione) -----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(team?.name || "Società Sportiva", pageW / 2, y, { align: "center" });
  y += 13;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90);
  const subParts = [team?.legal_name, team?.public_city].filter(Boolean);
  if (subParts.length) {
    doc.text(subParts.join(" — "), pageW / 2, y, { align: "center" });
    y += 12;
  }
  if (team?.figc_affiliation_code) {
    doc.text(`Matricola FIGC: ${team.figc_affiliation_code}`, pageW / 2, y, { align: "center" });
    y += 12;
  }
  doc.setTextColor(20);
  y += 12;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("MODULO CONTESTUALE", pageW / 2, y, { align: "center" });
  y += 16;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(
    "Autorizzazione congiunta degli esercenti la potestà genitoriale",
    pageW / 2,
    y,
    { align: "center" }
  );
  y += 26;

  // ----- DATI DEL MINORE -----
  sectionHeader(doc, "DATI DEL MINORE", margin, contentW, y);
  y += 18;
  const colGap = 16;
  const colW = (contentW - colGap) / 2;
  drawField(doc, "Cognome e Nome", `${player?.last_name || ""} ${player?.first_name || ""}`.trim(), margin, contentW, y);
  y += 30;
  drawField(doc, "Data di nascita", player?.birth_date || "", margin, colW, y);
  drawField(doc, "Luogo di nascita", player?.birth_place || "", margin + colW + colGap, colW, y);
  y += 30;
  drawField(doc, "Codice Fiscale", player?.fiscal_code || "", margin, contentW, y);
  y += 30;

  // ----- DATI DEI GENITORI (richiesto: nome + cognome di entrambi i genitori) -----
  y += 6;
  sectionHeader(doc, "DATI DEI GENITORI / ESERCENTI LA POTESTÀ", margin, contentW, y);
  y += 18;
  // Padre
  doc.setFontSize(8);
  doc.setTextColor(80);
  doc.text("PADRE (o tutore)", margin, y);
  y += 10;
  drawField(doc, "Cognome", "", margin, colW, y);
  drawField(doc, "Nome", "", margin + colW + colGap, colW, y);
  y += 30;
  drawField(doc, "Luogo e data di nascita", "", margin, colW, y);
  drawField(doc, "Codice Fiscale", "", margin + colW + colGap, colW, y);
  y += 32;

  // Madre
  doc.setFontSize(8);
  doc.setTextColor(80);
  doc.text("MADRE (o tutrice)", margin, y);
  y += 10;
  drawField(doc, "Cognome", "", margin, colW, y);
  drawField(doc, "Nome", "", margin + colW + colGap, colW, y);
  y += 30;
  drawField(doc, "Luogo e data di nascita", "", margin, colW, y);
  drawField(doc, "Codice Fiscale", "", margin + colW + colGap, colW, y);
  y += 38;

  // ----- DICHIARAZIONE -----
  // Page break safeguard
  if (y > 620) { doc.addPage(); y = 56; }
  sectionHeader(doc, "DICHIARAZIONE CONGIUNTA", margin, contentW, y);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(20);
  const intro =
    "I sottoscritti genitori esercenti la responsabilità genitoriale del minore sopra indicato, " +
    "consapevoli delle responsabilità civili e penali derivanti da dichiarazioni mendaci " +
    "(art. 76 D.P.R. 445/2000), in conformità all'art. 316 del Codice Civile e alle disposizioni " +
    "F.I.G.C. vigenti, dichiarano CONGIUNTAMENTE quanto segue:";
  const introLines = doc.splitTextToSize(intro, contentW);
  doc.text(introLines, margin, y);
  y += introLines.length * 12 + 8;

  const points = [
    "Autorizziamo il tesseramento del minore presso la società sportiva sopra indicata per la stagione sportiva in corso, accettando integralmente lo Statuto e il Regolamento Federale.",
    "Acconsentiamo all'utilizzo dell'immagine del minore per la sola attività istituzionale della società (sito web, social network ufficiali, locandine eventi), ai sensi del Reg. UE 679/2016 e del D. Lgs. 196/2003.",
    "Dichiariamo che il minore è in buone condizioni di salute e idoneo all'attività sportiva, allegando il certificato medico previsto dal D.M. 24/04/2013.",
    "Autorizziamo lo staff e i dirigenti accompagnatori a prestare le prime cure in caso di necessità durante le attività sportive.",
    "Solleviamo la società da responsabilità per fatti accaduti al di fuori degli orari di allenamento/gara e fuori dall'impianto sportivo.",
  ];
  doc.setFontSize(9);
  points.forEach((p, i) => {
    const lines = doc.splitTextToSize(`${i + 1}. ${p}`, contentW - 4);
    if (y + lines.length * 11 + 18 > 770) { doc.addPage(); y = 56; }
    doc.text(lines, margin + 2, y);
    y += lines.length * 11 + 6;
  });
  y += 6;

  // Note dissenso
  if (y > 660) { doc.addPage(); y = 56; }
  doc.setFontSize(7.5);
  doc.setTextColor(110);
  const note =
    "Nota: in caso di dissenso o di unico esercente la responsabilità genitoriale, " +
    "allegare provvedimento dell'Autorità Giudiziaria o autocertificazione che giustifichi " +
    "la sottoscrizione di un solo genitore (art. 337-ter c.c.).";
  const noteLines = doc.splitTextToSize(note, contentW);
  doc.text(noteLines, margin, y);
  y += noteLines.length * 10 + 18;
  doc.setTextColor(20);

  // ----- FIRME (due colonne, nessuna sovrapposizione) -----
  if (y > 680) { doc.addPage(); y = 56; }
  sectionHeader(doc, "FIRME", margin, contentW, y);
  y += 22;
  doc.setDrawColor(120);
  doc.setLineWidth(0.4);
  doc.line(margin, y, margin + colW, y);
  doc.line(margin + colW + colGap, y, margin + colW + colGap + colW, y);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("Firma del Padre", margin, y + 14);
  doc.text("Firma della Madre", margin + colW + colGap, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(120);
  doc.text("(o tutore esercente la potestà)", margin, y + 24);
  doc.text("(o tutrice esercente la potestà)", margin + colW + colGap, y + 24);
  doc.setTextColor(20);
  y += 42;

  // Luogo & data — su sua riga
  doc.setFontSize(9);
  doc.text("Luogo e data:", margin, y);
  doc.line(margin + 70, y + 1, margin + 300, y + 1);

  // ----- Footer in fondo pagina -----
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFontSize(7);
    doc.setTextColor(140);
    doc.text(
      `Generato il ${new Date().toLocaleString("it-IT")} · ${team?.name || ""} · Modulo contestuale F.I.G.C. - S.G.S.`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 24,
      { align: "center" }
    );
    doc.text(`Pag. ${p} / ${pages}`, pageW - margin, doc.internal.pageSize.getHeight() - 24, { align: "right" });
  }

  const safe = `contestuale_${player?.last_name || "x"}_${player?.first_name || "y"}`
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_");
  doc.save(`${safe}.pdf`);
}

function sectionHeader(doc, text, x, w, y) {
  doc.setFillColor(15, 118, 110); // emerald-700
  doc.rect(x, y - 12, w, 16, "F");
  doc.setTextColor(255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(text, x + 6, y);
  doc.setTextColor(20);
}

function drawField(doc, label, value, x, w, y) {
  doc.setFontSize(7.5);
  doc.setTextColor(120);
  doc.setFont("helvetica", "normal");
  doc.text(label, x, y);
  doc.setDrawColor(150);
  doc.setLineWidth(0.4);
  doc.line(x, y + 16, x + w, y + 16);
  if (value) {
    doc.setTextColor(20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    // Truncate to fit inside the line to avoid overflow
    const text = doc.splitTextToSize(String(value), w - 6)[0] || "";
    doc.text(text, x + 4, y + 14);
  }
  doc.setTextColor(20);
  doc.setFont("helvetica", "normal");
}
